import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { PrintLayoutFormValues } from "../printLayoutForm";
import type { ImageUploader } from "../useImageUpload";
import { ImageField } from "./ImageField";
import { PanelShell } from "./PanelShell";
import { ColorSwatchPicker } from "../ColorSwatchPicker";
import { TEXT_COLOR_OPTIONS } from "../printOptions";

const ALIGNS = [
  { value: "left", key: "left" },
  { value: "center", key: "center" },
  { value: "right", key: "right" },
] as const;

export function FooterPanel({ variant = "desktop" }: { variant?: "desktop" | "mobile" }) {
  const { t } = useTranslation("settings");
  const { register, watch, setValue, trigger, formState } = useFormContext<PrintLayoutFormValues>();
  const align = watch("footerAlign");
  const error = formState.errors.footerText?.message;

  return (
    <PanelShell title={t("printEditor.areas.footer")}>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="print-footer-text">{t("printEditor.footer.text")}</Label>
        <Input
          id="print-footer-text"
          invalid={!!error}
          aria-describedby={error ? "print-footer-hint print-footer-error" : "print-footer-hint"}
          {...register("footerText")}
        />
        <p id="print-footer-hint" className="text-xs text-muted-foreground">
          {t("printEditor.footer.hint")}
        </p>
        {error ? (
          <p id="print-footer-error" role="alert" className="text-xs text-destructive">
            {String(error)}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        <Label id="print-footer-align">{t("printEditor.footer.align")}</Label>
        <div role="group" aria-labelledby="print-footer-align" className="grid grid-cols-3 gap-2">
          {ALIGNS.map(({ value, key }) => (
            <button
              key={value}
              type="button"
              aria-pressed={align === value}
              onClick={() => setValue("footerAlign", value, { shouldDirty: true })}
              className={cn(
                "rounded-xl border py-2.5 text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                align === value && "border-2 border-primary bg-accent font-semibold",
              )}
            >
              {t(`printEditor.footer.${key}`)}
            </button>
          ))}
        </div>
      </div>

      <ColorSwatchPicker
        variant={variant}
        label={t("printEditor.footer.color")}
        value={watch("footerTextColor")}
        options={TEXT_COLOR_OPTIONS}
        onChange={(hex) => setValue("footerTextColor", hex, { shouldDirty: true, shouldValidate: true })}
        onBlur={() => void trigger("footerTextColor")}
        error={formState.errors.footerTextColor?.message}
      />
    </PanelShell>
  );
}
